import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import {
    getOrdenesTrabajo, addOrdenTrabajo, cambiarEstadoOrdenTrabajo,
    getOrdenTrabajoHistorial, getOrdenTrabajoItems, addOrdenTrabajoItem,
    toggleOrdenTrabajoItem, deleteOrdenTrabajoItem,
    getOrdenTrabajoDocumentos, addOrdenTrabajoDocumento, deleteOrdenTrabajoDocumento,
    deleteOrdenTrabajo,
} from '../service/OrdenTrabajoService';

const HOST_API = process.env.REACT_APP_URL;

const ESTADOS = ['Pendiente', 'En Proceso', 'Finalizada', 'Cancelada'];
const ESTADO_OPTIONS = ESTADOS.map(e => ({ label: e, value: e }));
const ESTADO_COLORS = {
    'Pendiente':  { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
    'En Proceso': { bg: '#eff8ff', color: '#175cd3', border: '#b2ddff' },
    'Finalizada': { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    'Cancelada':  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
};

const fmtFecha = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtFechaHora = (v) => v ? new Date(v).toLocaleString('es-AR') : '—';

const initialFormData = {
    reclamoId: null,
    tipoReclamoNombre: null,
    titulo: '',
    descripcion: '',
    responsable: '',
    fechaLimite: null,
};

const estadoBadge = (estado) => {
    const s = ESTADO_COLORS[estado] || ESTADO_COLORS['Pendiente'];
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
        }}>
            {estado}
        </span>
    );
};

const OrdenesTrabajoAdmin = () => {
    const toast = useRef(null);
    const location = useLocation();
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [ordenes, setOrdenes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState('');
    const [globalFilter, setGlobalFilter] = useState('');

    const [nuevoVisible, setNuevoVisible] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [guardando, setGuardando] = useState(false);

    const [detalleVisible, setDetalleVisible] = useState(false);
    const [ordenActiva, setOrdenActiva] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [items, setItems] = useState([]);
    const [documentos, setDocumentos] = useState([]);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [nuevoItemTexto, setNuevoItemTexto] = useState('');
    const [nuevoEstado, setNuevoEstado] = useState('');
    const [comentarioEstado, setComentarioEstado] = useState('');
    const [cambiandoEstado, setCambiandoEstado] = useState(false);
    const [subiendoDocumento, setSubiendoDocumento] = useState(false);

    const fetchOrdenes = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getOrdenesTrabajo(barrio.id, administracionId);
            setOrdenes(data ?? []);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las órdenes de trabajo', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchOrdenes(); }, [fetchOrdenes]);

    // Si venimos de "Generar orden de trabajo" desde un Reclamo, precargamos el alta.
    useEffect(() => {
        if (location.state?.desdeReclamo) {
            setFormData({
                ...initialFormData,
                reclamoId: location.state.reclamoId,
                tipoReclamoNombre: location.state.tipoReclamoNombre,
                titulo: location.state.tipoReclamoNombre || '',
                descripcion: location.state.descripcion || '',
            });
            setNuevoVisible(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nuevaOrden = () => {
        setFormData(initialFormData);
        setNuevoVisible(true);
    };

    const guardarOrden = async () => {
        if (!formData.titulo?.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese un título', life: 3000 });
            return;
        }
        setGuardando(true);
        try {
            await addOrdenTrabajo({
                barrioId: barrio.id,
                administracionId,
                reclamoId: formData.reclamoId || null,
                titulo: formData.titulo,
                descripcion: formData.descripcion,
                responsable: formData.responsable,
                fechaLimite: formData.fechaLimite,
                usuarioCreacionId: usuarioId,
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Orden de trabajo creada correctamente', life: 3000 });
            setNuevoVisible(false);
            fetchOrdenes();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo crear la orden de trabajo', life: 4000 });
        } finally {
            setGuardando(false);
        }
    };

    const cargarDetalle = useCallback(async (ordenTrabajoId) => {
        const [histRes, itemsRes, docsRes] = await Promise.all([
            getOrdenTrabajoHistorial(ordenTrabajoId),
            getOrdenTrabajoItems(ordenTrabajoId),
            getOrdenTrabajoDocumentos(ordenTrabajoId),
        ]);
        setHistorial(histRes.data ?? []);
        setItems(itemsRes.data ?? []);
        setDocumentos(docsRes.data ?? []);
    }, []);

    const abrirDetalle = async (orden) => {
        setOrdenActiva(orden);
        setNuevoEstado(orden.estado);
        setComentarioEstado('');
        setDetalleVisible(true);
        setLoadingDetalle(true);
        try {
            await cargarDetalle(orden.ordenTrabajoId);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el detalle', life: 3000 });
        } finally {
            setLoadingDetalle(false);
        }
    };

    const aplicarCambioEstado = async () => {
        if (!ordenActiva) return;
        setCambiandoEstado(true);
        try {
            await cambiarEstadoOrdenTrabajo({
                ordenTrabajoId: ordenActiva.ordenTrabajoId,
                administracionId,
                estado: nuevoEstado,
                texto: comentarioEstado,
                usuarioId,
            });
            setComentarioEstado('');
            setOrdenActiva(prev => ({ ...prev, estado: nuevoEstado }));
            await cargarDetalle(ordenActiva.ordenTrabajoId);
            fetchOrdenes();
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Estado actualizado', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el estado', life: 3000 });
        } finally {
            setCambiandoEstado(false);
        }
    };

    const agregarItem = async () => {
        if (!nuevoItemTexto.trim() || !ordenActiva) return;
        try {
            await addOrdenTrabajoItem(ordenActiva.ordenTrabajoId, nuevoItemTexto.trim(), items.length);
            setNuevoItemTexto('');
            await cargarDetalle(ordenActiva.ordenTrabajoId);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo agregar el item', life: 3000 });
        }
    };

    const tildarItem = async (item) => {
        try {
            await toggleOrdenTrabajoItem(item.ordenTrabajoItemId, !item.hecho);
            await cargarDetalle(ordenActiva.ordenTrabajoId);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el item', life: 3000 });
        }
    };

    const borrarItem = async (item) => {
        try {
            await deleteOrdenTrabajoItem(item.ordenTrabajoItemId);
            await cargarDetalle(ordenActiva.ordenTrabajoId);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el item', life: 3000 });
        }
    };

    const subirDocumentos = async (event) => {
        if (!ordenActiva) return;
        const files = event.files || [];
        if (files.length === 0) return;
        setSubiendoDocumento(true);
        try {
            for (const file of files) {
                await addOrdenTrabajoDocumento(ordenActiva.ordenTrabajoId, administracionId, barrio.id, usuarioId, file);
            }
            await cargarDetalle(ordenActiva.ordenTrabajoId);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Documentos subidos correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron subir uno o más documentos', life: 3000 });
        } finally {
            setSubiendoDocumento(false);
        }
    };

    const borrarDocumento = (doc) => {
        confirmDialog({
            message: `¿Eliminar "${doc.nombreArchivo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteOrdenTrabajoDocumento(doc.ordenTrabajoDocumentoId);
                    await cargarDetalle(ordenActiva.ordenTrabajoId);
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el documento', life: 3000 });
                }
            },
        });
    };

    const eliminarOrden = (orden) => {
        confirmDialog({
            message: `¿Eliminar la orden de trabajo "${orden.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteOrdenTrabajo(orden.ordenTrabajoId, administracionId);
                    toast.current.show({ severity: 'success', summary: 'Eliminada', detail: 'Orden de trabajo eliminada', life: 3000 });
                    fetchOrdenes();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar la orden de trabajo', life: 3000 });
                }
            },
        });
    };

    const ordenesFiltradas = ordenes.filter(o => !filtroEstado || o.estado === filtroEstado);

    const accionesTemplate = (o) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
            <Button icon="pi pi-eye" className="p-button-rounded p-button-text p-button-sm"
                tooltip="Ver detalle" tooltipOptions={{ position: 'top' }}
                onClick={() => abrirDetalle(o)} />
            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                tooltip="Eliminar" tooltipOptions={{ position: 'top' }}
                onClick={() => eliminarOrden(o)} />
        </div>
    );

    return (
        <>
            <h4>Órdenes de Trabajo</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2">
                    <Dropdown value={filtroEstado} options={[{ label: 'Todos', value: '' }, ...ESTADO_OPTIONS]}
                        onChange={(e) => setFiltroEstado(e.value)} placeholder="Estado" className="p-inputtext-sm" style={{ minWidth: 160 }} />
                    <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Buscar..." className="p-inputtext-sm" />
                </div>
                <Button label="Nueva OT" icon="pi pi-plus" className="p-button-sm" onClick={nuevaOrden} />
            </div>

            <DataTable value={ordenesFiltradas} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                showGridlines responsiveLayout="scroll" globalFilter={globalFilter}
                globalFilterFields={['titulo', 'responsable', 'descripcion']}
                emptyMessage="No hay órdenes de trabajo para este barrio"
                className="p-datatable-gridlines bg-white"
                sortField="fechaCreacion" sortOrder={-1}>
                <Column field="titulo" header="Título" sortable />
                <Column field="responsable" header="Responsable" sortable body={(r) => r.responsable || '—'} />
                <Column header="Estado" body={(r) => estadoBadge(r.estado)} style={{ width: '9rem', textAlign: 'center' }} />
                <Column field="fechaLimite" header="Fecha Límite" sortable body={(r) => fmtFecha(r.fechaLimite)} style={{ width: '9rem' }} />
                <Column field="fechaCreacion" header="Creada" sortable body={(r) => fmtFecha(r.fechaCreacion)} style={{ width: '9rem' }} />
                <Column header="Reclamo" body={(r) => r.reclamoId ? `#${r.reclamoId} — ${r.tipoReclamoNombre || ''}` : '—'} style={{ width: '11rem' }} />
                <Column header="Acciones" body={accionesTemplate} style={{ width: '7rem', textAlign: 'center' }} />
            </DataTable>

            {/* Alta */}
            <Dialog header="Nueva Orden de Trabajo" visible={nuevoVisible} style={{ width: '500px' }} modal
                onHide={() => setNuevoVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setNuevoVisible(false)} disabled={guardando} />
                        <Button label="Crear" icon="pi pi-check" className="p-button-sm" onClick={guardarOrden} loading={guardando} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {formData.reclamoId && (
                        <div className="col-12">
                            <span style={{ background: '#eff8ff', color: '#175cd3', border: '1px solid #b2ddff', borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.85rem' }}>
                                Se va a generar a partir del reclamo #{formData.reclamoId}
                            </span>
                        </div>
                    )}
                    <div className="col-12">
                        <label style={lbl}>Título *</label>
                        <InputText value={formData.titulo} onChange={(e) => setFormData(f => ({ ...f, titulo: e.target.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Descripción</label>
                        <InputTextarea value={formData.descripcion} onChange={(e) => setFormData(f => ({ ...f, descripcion: e.target.value }))} rows={3} className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Responsable</label>
                        <InputText value={formData.responsable} onChange={(e) => setFormData(f => ({ ...f, responsable: e.target.value }))} placeholder="Nombre del técnico o empresa" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha Límite</label>
                        <Calendar value={formData.fechaLimite} onChange={(e) => setFormData(f => ({ ...f, fechaLimite: e.value }))} dateFormat="dd/mm/yy" showIcon className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>

            {/* Detalle */}
            <Dialog header={ordenActiva?.titulo || 'Orden de Trabajo'} visible={detalleVisible} style={{ width: '750px' }} modal
                onHide={() => setDetalleVisible(false)}>
                <LoadingOverlay visible={loadingDetalle} />
                {!loadingDetalle && ordenActiva && (
                    <TabView>
                        <TabPanel header="General">
                            <div className="grid p-fluid mt-1">
                                <div className="col-12">
                                    <b>Descripción:</b>
                                    <p style={{ marginTop: 4, color: '#475467' }}>{ordenActiva.descripcion || 'Sin descripción'}</p>
                                </div>
                                {ordenActiva.reclamoId && (
                                    <div className="col-12">
                                        <span style={{ background: '#eff8ff', color: '#175cd3', border: '1px solid #b2ddff', borderRadius: 8, padding: '0.35rem 0.7rem', fontSize: '0.85rem' }}>
                                            Generada desde el reclamo #{ordenActiva.reclamoId} — {ordenActiva.tipoReclamoNombre}
                                        </span>
                                    </div>
                                )}
                                <div className="col-6">
                                    <label style={lbl}>Responsable</label>
                                    <div>{ordenActiva.responsable || '—'}</div>
                                </div>
                                <div className="col-6">
                                    <label style={lbl}>Fecha Límite</label>
                                    <div>{fmtFecha(ordenActiva.fechaLimite)}</div>
                                </div>

                                <div className="col-12 mt-3" style={{ borderTop: '1px solid #eaecf0', paddingTop: '1rem' }}>
                                    <label style={lbl}>Cambiar estado</label>
                                    <div className="flex gap-2 align-items-start flex-wrap">
                                        <Dropdown value={nuevoEstado} options={ESTADO_OPTIONS} onChange={(e) => setNuevoEstado(e.value)} className="p-inputtext-sm" style={{ minWidth: 160 }} />
                                        <InputText value={comentarioEstado} onChange={(e) => setComentarioEstado(e.target.value)} placeholder="Comentario (opcional)" className="p-inputtext-sm" style={{ flex: 1, minWidth: 180 }} />
                                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={aplicarCambioEstado} loading={cambiandoEstado} />
                                    </div>
                                </div>
                            </div>
                        </TabPanel>

                        <TabPanel header={`Checklist (${items.filter(i => i.hecho).length}/${items.length})`}>
                            <div className="flex gap-2 mb-3">
                                <InputText value={nuevoItemTexto} onChange={(e) => setNuevoItemTexto(e.target.value)}
                                    placeholder="Nueva tarea..." className="p-inputtext-sm" style={{ flex: 1 }}
                                    onKeyDown={(e) => e.key === 'Enter' && agregarItem()} />
                                <Button icon="pi pi-plus" className="p-button-sm" onClick={agregarItem} />
                            </div>
                            {items.length === 0 ? (
                                <p style={{ color: '#98a2b3' }}>Sin tareas cargadas todavía</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {items.map((item) => (
                                        <li key={item.ordenTrabajoItemId} className="flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid #f2f4f7' }}>
                                            <Checkbox checked={item.hecho} onChange={() => tildarItem(item)} />
                                            <span style={{ flex: 1, textDecoration: item.hecho ? 'line-through' : 'none', color: item.hecho ? '#98a2b3' : '#344054' }}>
                                                {item.descripcion}
                                            </span>
                                            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm" onClick={() => borrarItem(item)} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </TabPanel>

                        <TabPanel header={`Documentos (${documentos.length})`}>
                            <FileUpload
                                name="documentos"
                                multiple
                                accept=".pdf,.jpg,.jpeg,.png"
                                maxFileSize={5000000}
                                customUpload
                                uploadHandler={subirDocumentos}
                                chooseLabel="Seleccionar archivos"
                                uploadLabel="Subir"
                                cancelLabel="Cancelar"
                                disabled={subiendoDocumento}
                            />
                            {documentos.length === 0 ? (
                                <p style={{ color: '#98a2b3', marginTop: '1rem' }}>Sin documentos adjuntos todavía</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0' }}>
                                    {documentos.map((doc) => (
                                        <li key={doc.ordenTrabajoDocumentoId} className="flex align-items-center gap-2 py-2" style={{ borderBottom: '1px solid #f2f4f7' }}>
                                            <i className="pi pi-file" style={{ color: '#667085' }} />
                                            <a href={`${HOST_API}${doc.url.replace(/^\//, '')}`} target="_blank" rel="noreferrer" style={{ flex: 1, color: '#175cd3' }}>
                                                {doc.nombreArchivo}
                                            </a>
                                            <span style={{ color: '#98a2b3', fontSize: '0.8rem' }}>{fmtFechaHora(doc.fecha)}</span>
                                            <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-danger p-button-sm" onClick={() => borrarDocumento(doc)} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </TabPanel>

                        <TabPanel header="Historial">
                            {historial.length === 0 ? (
                                <p style={{ color: '#98a2b3' }}>Sin historial</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {historial.map((h) => {
                                        const s = ESTADO_COLORS[h.estado] || ESTADO_COLORS['Pendiente'];
                                        return (
                                            <li key={h.ordenTrabajoHistorialId} className="flex gap-3 py-3" style={{ borderBottom: '1px solid #f2f4f7' }}>
                                                <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, marginTop: 6, flexShrink: 0 }} />
                                                <div style={{ flex: 1 }}>
                                                    <div>
                                                        {estadoBadge(h.estado)}
                                                        <span style={{ marginLeft: 8, fontWeight: 600, fontSize: '0.85rem' }}>{h.usuarioNombre}</span>
                                                    </div>
                                                    {h.texto && <div style={{ marginTop: 4, color: '#475467' }}>{h.texto}</div>}
                                                    <div style={{ marginTop: 4, color: '#98a2b3', fontSize: '0.78rem' }}>{fmtFechaHora(h.fecha)}</div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </TabPanel>
                    </TabView>
                )}
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default OrdenesTrabajoAdmin;
